import { ONE_MINUTE_IN_MS } from '@careerstart/wae-common/src/main/constants/timeConversions';

import { getTimeZone, minutesFromMidnightToReadableTime } from '../../utils';
import { getSundayOfWeek } from '../../utils/timeConverter';

import { MAX_JOBS_TOTAL, TIME_FIELDS_ERRORS } from './constDicts';

export const isStartDateAndEndDateEqual = (formVals) => {
  const startVal = formVals?.dateRange?.[0];
  const endVal = formVals?.dateRange?.[1];

  if (!startVal || !endVal) {
    return false;
  }

  const startDate = new Date(startVal).getTime();
  const endDate = new Date(endVal).getTime();
  if (!startDate || !endDate) return false;

  return startDate === endDate;
};

/**
 *
 * @param {Object} daysOfWeek {dayOfWeek: boolean}
 * @returns {Array<string>} Returns a list of strings
 */
export const getCadence = (daysOfWeek, formValues) => {
  const selectedDays =
    daysOfWeek &&
    Object.keys(daysOfWeek)
      .filter((day) => daysOfWeek[day])
      .map((day) => day.toLowerCase());

  // `daysOfWeek` can be an empty object when the Cadence section is hidden (single-day jobs).
  // In that case, fall back to the weekday implied by the selected date.
  if (selectedDays?.length) {
    return selectedDays;
  }

  if (isStartDateAndEndDateEqual(formValues)) {
    const date = new Date(formValues?.dateRange[0]);
    const options = { weekday: 'long' };
    return [date.toLocaleDateString('en-US', options).toLowerCase()];
  }

  return [];
};

export const customShift = {
  name: 'customShift',
};

export const getAllValidShiftTimes = () => {
  const returnArray = [];

  const numberOf5MinOccurances = (24 * 60) / 5 - 1; // 24 hours * 60 minutes / 5 minutes - last occurance
  for (let i = 0; i <= numberOf5MinOccurances; i += 1) {
    returnArray.push({
      value: i * 5,
      displayLabel: minutesFromMidnightToReadableTime(i * 5),
      _id: minutesFromMidnightToReadableTime(i * 5),
      key: i * 5,
    });
  }

  return returnArray;
};

export const generateEndDate = (endDate, isOvernight) => {
  const newEndDate = new Date(endDate);

  if (isOvernight) {
    newEndDate.setDate(newEndDate.getDate() + 1);
  }

  newEndDate.setHours(0, 0, 0, 0);

  return newEndDate.getTime();
};

export const getExtensionGroupPayload = ({ formValues }) => {
  // Note: despite the legacy name, this now returns a payload compatible with
  // `create-job-order.v2.post.req` for the `/v2/create-job-order` endpoint.
  // Per request, we intentionally ignore autofill / invite-workers fields for now.

  const positionTemplateId = formValues?.positionTemplate?._id;
  const locationId = formValues?.location?._id;
  const departmentId = formValues?.department?._id;

  const startMinutes = formValues?.startTime?.value;
  const endMinutes = formValues?.endTime?.value;
  const numOpenings = parseInt(formValues?.numOpenings, 10);
  const numOverstaff =
    formValues?.numOverstaff === null ||
    formValues?.numOverstaff === undefined ||
    formValues?.numOverstaff === ''
      ? undefined
      : parseInt(formValues?.numOverstaff, 10);

  const startDateValue = formValues?.dateRange?.[0] ?? null;
  const endDateValue = formValues?.dateRange?.[1] ?? null;

  const startDate = startDateValue ? new Date(startDateValue) : new Date();
  const scheduleStart = new Date(startDate);
  scheduleStart.setHours(0, 0, 0, 0);
  const scheduleStartEpoch = scheduleStart.getTime();

  const scheduleEndDate = endDateValue ? new Date(endDateValue) : new Date(scheduleStart);

  const scheduleEnd =
    generateEndDate(scheduleEndDate, startMinutes > endMinutes) + endMinutes * ONE_MINUTE_IN_MS;

  const anchorDate = getSundayOfWeek(scheduleStart).getTime();

  const dayNameToOffset = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
  };

  // Use `getCadence` so single-day jobs still produce one occurrence even if the
  // Cadence section is hidden and `daysOfWeek` is `{}`.
  const cadence = getCadence(formValues?.daysOfWeek, formValues);

  const occurrences = cadence
    .map((dayName) => ({
      offsetDay: dayNameToOffset[dayName],
      shifts: [
        {
          startMinutes,
          endMinutes,
          numOpenings,
        },
      ],
    }))
    .filter((o) => typeof o.offsetDay === 'number')
    .sort((a, b) => a.offsetDay - b.offsetDay);

  // Build initial job orders within the schedule window.
  // This matches the documented behavior: `jobOrders` controls creation, and `pattern` (if present)
  // stores the recurrence on the extension group.
  const selectedOffsets = new Set(occurrences.map((o) => o.offsetDay));
  const jobOrders = [];

  const cursor = new Date(scheduleStart);
  cursor.setHours(0, 0, 0, 0);

  const endCursor = new Date(scheduleEndDate);
  endCursor.setHours(0, 0, 0, 0);

  while (cursor <= endCursor) {
    const offsetDay = (cursor.getDay() + 7) % 7; // 0=Sunday ... 6=Saturday
    if (selectedOffsets.has(offsetDay)) {
      const start = new Date(cursor);
      start.setHours(Math.floor(startMinutes / 60), startMinutes % 60, 0, 0);

      const end = new Date(cursor);
      end.setHours(Math.floor(endMinutes / 60), endMinutes % 60, 0, 0);

      // Overnight shift
      if (end < start) {
        end.setDate(end.getDate() + 1);
      }

      jobOrders.push({
        start: start.getTime(),
        end: end.getTime(),
        positionTemplateId,
        departmentId,
        locationId,
        numOpenings,
        ...(Number.isInteger(numOverstaff) && numOverstaff >= 0 ? { numOverstaff } : {}),
      });
    }

    cursor.setDate(cursor.getDate() + 1);
  }

  return {
    jobOrders,
    pattern: {
      timeZone: getTimeZone(),
      cycle: { lengthDays: 7, anchorDate },
      occurrences,
      jobOrderDefaults: {
        positionTemplateId,
        departmentId,
        locationId,
      },
      scheduleWindow: { start: scheduleStartEpoch, end: scheduleEnd },
    },
  };
};

export const getEndDateFieldError = ({ value }) => {
  if (!value) {
    return 'error.field.empty';
  }

  if (!value[1]) {
    return 'error.field.empty';
  }
  const startEpoch = new Date(value[0]).getTime();
  const endEpoch = new Date(value[1]).getTime();

  // Date is not valid - cannot convert to epoch
  if (Number.isNaN(endEpoch)) {
    return 'error.date.invalid';
  }

  if (endEpoch < startEpoch) {
    return 'error.dates.inverted';
  }

  return undefined;
};

export const getStartFieldError = ({ value }) => {
  if (!value) {
    return 'error.field.empty';
  }

  const startEpoch = new Date(value[0]).getTime();

  // Date is not valid - cannot convert to epoch
  if (!startEpoch) {
    return 'error.date.invalid';
  }

  return undefined;
};

export const isValidDateRange = (value) =>
  getStartFieldError({ value }) || getEndDateFieldError({ value });

const getShiftLengthInMinutes = ({ start, end }) => {
  let duration; // Calculate the duration based on whether endTime is before or after startTime
  const minutesInDay = 24 * 60;
  if (end >= start) {
    // If end is after or at start, the duration is straightforward
    duration = end - start;
  } else {
    // If end is before start, it means the range spans over midnight
    duration = minutesInDay - start + end;
  }
  return duration;
};

export const shiftTimeValidation = (val, formVals) => {
  if (!val) {
    return 'error.field.empty';
  }

  const maxDurationMinutes = 20 * 60;

  const duration = getShiftLengthInMinutes({
    start: formVals?.startTime?.value,
    end: formVals?.endTime?.value,
  });

  // Check if the duration is less than 20 hours
  if (duration > maxDurationMinutes || duration === 0) {
    return TIME_FIELDS_ERRORS.shiftLength;
  }
  return null;
};

export const isWeekOT = (formVals) => {
  if (isStartDateAndEndDateEqual(formVals)) {
    return false;
  }
  const cad = getCadence(formVals?.daysOfWeek || {});
  const shiftMins = getShiftLengthInMinutes({
    start: formVals?.startTime?.value,
    end: formVals?.endTime?.value,
  });

  const otMarkerInMinutes = 40 * 60; // 40hrs * 60min
  if (shiftMins * cad.length > otMarkerInMinutes) {
    return true;
  }
  return false;
};

/**
 * Validates job creation form data for bulk day job creation
 * @param {Array} datesOpenings - Array of date/opening objects
 * @returns {Object} - { isValid: boolean, message?: string }
 */
export const validateJobCreation = (datesOpenings) => {
  if (!datesOpenings || datesOpenings.length === 0) {
    return { isValid: false, message: 'job.create.validation.selectAtLeastOneDate' };
  }

  if (datesOpenings.length > MAX_JOBS_TOTAL) {
    return { isValid: false, message: 'job.create.validation.maxJobsPerMonth' };
  }

  const invalidDates = datesOpenings.filter((item) => !item.openings || item.openings < 1);
  if (invalidDates.length > 0) {
    return { isValid: false, message: 'job.create.validation.eachDateMustHaveOpening' };
  }

  return { isValid: true };
};
